"use client";

import { useEffect } from "react";
import Script from "next/script";
import { useConsent } from "./consent";

declare global {
  interface Window {
    clarity?: (...args: unknown[]) => void;
  }
}

/**
 * Microsoft Clarity, gated on the same consent as Google Analytics.
 *
 * Clarity records session replays and builds heatmaps — it captures mouse
 * movement, clicks, scroll depth and page structure, which is a good deal more
 * than GA4's event counts. That makes the consent gate load-bearing, not a
 * formality: nothing is requested from clarity.ms until a visitor accepts, so
 * a visitor who declines is never recorded and generates no request to
 * Microsoft at all.
 *
 * Clarity ships its own `clarity('consent')` API for the opposite pattern —
 * load the tag first, ask permission after. We do not use it. Not loading the
 * script is a stronger guarantee than loading it and asking it to behave, and
 * it is what /privacy Section 8 tells visitors we do.
 *
 * The snippet Microsoft hands out is a raw inline <script>. This is the same
 * code, wrapped in next/script with `afterInteractive`: the App Router streams
 * its HTML, so a hand-written inline script is not guaranteed to run in the
 * order it appears, and this keeps the tag off the critical path.
 *
 * The project ID comes from NEXT_PUBLIC_CLARITY_ID and is deliberately absent
 * from .env.local, so local development is never recorded.
 */
export function Clarity() {
  const { consent } = useConsent();
  const id = process.env.NEXT_PUBLIC_CLARITY_ID;

  if (!id || consent !== "granted") return null;

  // Split so the effect below can own a real mount/unmount boundary — hooks
  // cannot sit after the early return above.
  return <ClarityTag id={id} />;
}

function ClarityTag({ id }: { id: string }) {
  // Withdrawing consent unmounts this component, but that alone is not enough.
  // next/script does not remove the <script> it injected, and the recorder
  // Clarity already started keeps capturing until the next page load — so
  // "withdrawing stops analytics immediately" would be false without this.
  // The command goes through Clarity's own queue stub, so it is a harmless
  // no-op if the tag never finished loading.
  useEffect(() => {
    return () => {
      try {
        window.clarity?.("stop");
      } catch {
        /* the tag is third-party; a failure here must not break the page */
      }
    };
  }, []);

  return (
    <Script id="ms-clarity" strategy="afterInteractive">
      {`(function(c,l,a,r,i,t,y){
    c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
    t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
    y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
})(window, document, "clarity", "script", ${JSON.stringify(id)});`}
    </Script>
  );
}
